/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { InstanceState } from '../../../../ts/instance';
import { ReactivateSession } from '../../../../ts/session';
import { endpoints } from '../../util/config';

export const getAuthStatus = async (): Promise<void> => {
  return axios.get(endpoints.authenticate);
};

export const getReactivateSessionQuery = (
  reactivateSession: ReactivateSession | undefined
): string =>
  reactivateSession === undefined
    ? ''
    : `reactivateSession=${reactivateSession}`;

export const fetchSessionData = async (forceReactivateSession: boolean) => {
  try {
    // The value "force" will force the inactivity session to be reactivated,
    // and the value "never" will make sure the inactivity session is never
    // reactivated due to this call.
    const queryParameter = getReactivateSessionQuery(
      forceReactivateSession ? ReactivateSession.FORCE : ReactivateSession.NEVER
    );

    const { data } = await axios.get(
      `${endpoints.store}/session?${queryParameter}`
    );

    const {
      session: { inactivityExpiration }
    } = data;

    // Covert the inactivity expiration time (expressed in milliseconds)
    // into minutes (or leave it as undefined if the value we get is not numeric).
    return Number.isInteger(inactivityExpiration)
      ? Math.round((inactivityExpiration - Date.now()) / 1000 / 60)
      : undefined;
  } catch (error) {
    console.error('Error fetching session data', error);
    return undefined;
  }
};

export const getInstanceState = async (
  region: string,
  instance: string
): Promise<InstanceState> => {
  const response = await axios.get<InstanceState>(
    `${endpoints.authenticate}/instanceState`,
    {
      params: {
        region,
        instance
      }
    }
  );
  return response.data;
};

export const refreshToken = async (): Promise<string> => {
  const response = await axios.post(
    urlJoin(endpoints.authenticate, '/user/renewToken'),
    {
      responseType: 'text'
    }
  );
  return response.data;
};
